import { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"

import { showLoading } from 'services/util'

import { graphql } from '../../api'
import { adminMakePaymentMutation, adminUpdateRegistrationMutation, updateAuctionItemMutation, updateFixedItemMutation } from '../../graphql/Mutations'
import { adminGetAuctionByCode, buildManageCheckoutBidItemRow, buildManageCheckoutDonationRow, buildManageCheckoutFixedItemRow } from '../auctionsadminutil'
import { sortSpan, handleSort, performSort } from '../auctionsadminutil'

import css from './sass/closedauction.module.scss'
import themeCss from '../../components/sass/theme.module.scss'
import ClosedManageCheckoutRow from './ClosedManageCheckoutRow'
import ConfirmMakePayment from '../ConfirmModal/ConfirmMakePayment'
import ConfirmMarkAsPaid from '../ConfirmModal/ConfirmMarkAsPaid'
import ConfirmMarkFixedItemAsPaid from '../ConfirmModal/ConfirmMarkFixedItemAsPaid'
import ConfirmMarkDonationAsPaid from '../ConfirmModal/ConfirmMarkDonationAsPaid'
import { useSelector } from 'react-redux'
import { getLoggedInUser } from 'redux/selectors'

export default function ClosedManageCheckout(props) {
  const [itemsPaidCount, setItemsPaidCount] = useState(0)
  const [itemsUnpaidCount, setItemsUnpaidCount] = useState(0)

  const [selectedItems, setSelectedItems] = useState([])
  const [selectedFixedItems, setSelectedFixedItems] = useState([])
  const [selectedDonation, setSelectedDonation] = useState(false)
  const [chargeTotal, setChargeTotal] = useState(0)

  //confirm modal variables
  const [modalUse, setModalUse] = useState()
  const [modalData, setModalData] = useState()
  const [showModal, setShowModal] = useState(false)

  //arrays of truth for all attendee balances in auction
  const [attendeeItems, setAttendeeItems] = useState([])
  const [attendeeFixedItems, setAttendeeFixedItems] = useState([])
  const [attendeeDonation, setAttendeeDonation] = useState(null)
  const [attendeeCheckoutRows, setAttendeeCheckoutRows] = useState([])

  const [sortOptions, setSortOptions] = useState({ key: '', dir: 'asc' })
  const tableFormatter = [
    ['Title', 'Item Name'],
    ['Quantity', 'Quantity'],
    ['AmountDue', 'Item Amount Due'],
    ['Paid', 'Paid']
  ]

  //to refresh query on changes
  const queryClient = useQueryClient()

  const {auctionCode, managedAttendee, toAllAttendeePage} = props

  const userId = managedAttendee?.User.UserId
  const userCrmId = managedAttendee?.CrmId
  const userRegistrationId = managedAttendee?.RegistrationId

  const loggedInUser = useSelector(getLoggedInUser)

  // load auction data by code, so it will work for deep-linking
  const { data: auctionDetail, error, isLoading } = useQuery({
    queryKey: ["adminAuctionDetail", auctionCode],
    queryFn: async () => {
      return await adminGetAuctionByCode(auctionCode)
    },
    staleTime: 0
  })

  const {data: updateItemResponse, error: updateItemError, isPending, mutate: updateItem } = useMutation({
    mutationFn: (input) => {
      return graphql({query: updateAuctionItemMutation, variables: {input} })
    },
  })

  const {data: updateFixedItemResponse, error: updateFixedItemError, isPending: isFixedItemPending, mutate: updateFixedItem } = useMutation({
      mutationFn: (input) => {
        return graphql({query: updateFixedItemMutation, variables: {input} })
      },
    })

  const {data: updateDonationResponse, error: updateDonationError, isPending: isDonationPending, mutate: updateDonation } = useMutation({
      mutationFn: (input) => {
        return graphql({query: adminUpdateRegistrationMutation, variables: input })
      },
    })

  const {data: adminMakePaymentResponse, error: adminMakePaymentError, isPending: adminMakePaymentPending, mutate: adminCharge } = useMutation({
    mutationFn: (input) => {
      return graphql({query: adminMakePaymentMutation, variables: {...input} })
    },
  })

  //use effect for query results
  useEffect(() => {
    showLoading(isLoading)

    if (error || (auctionDetail && auctionDetail.error)) {
      console.log('Error!', error || auctionDetail.error)
    }

  }, [auctionDetail, error, isLoading])

  const markAsPaid = () => {
    const data = {
      totalMarkedPaid: chargeTotal,
      loggedInUser: loggedInUser,
      attendeeName
    }

    if (selectedItems.length === 1) {
      data['itemsMarkedPaid'] = selectedItems
      data['auctionDetail'] = auctionDetail
      setModalUse('markItemAsPaid')
    }
    if (selectedFixedItems.length === 1) {
      data['fixedItemsMarkedPaid'] = selectedFixedItems
      data['userId'] = userId
      data['auctionDetail'] = auctionDetail
      setModalUse('markFixedItemAsPaid')
    }
    if (selectedDonation) {
      data['donationsMarkedPaid'] = selectedDonation
      data['registrationId'] = userRegistrationId
      setModalUse('markDonationAsPaid')
    }
    
    setModalData(data)
    setShowModal(true)
  }

  const chargeAmountDueAction = () => {
    const data = {
      auctionId: auctionDetail.AuctionId,
      itemsCharged: selectedItems,
      fixedItemsCharged: selectedFixedItems,
      payDonation: selectedDonation,
      totalCharged: chargeTotal,
      totalSelectedCount: countItems(),
      endUserId: userId,
      endUserCrmId: userCrmId,
    }

    setModalData(data)
    setModalUse('confirmCharges')
    setShowModal(true)
  }

  //for update item
  useEffect(() => {
    showLoading(isPending)

    if (updateItemError) {
      console.log("Item not updated: ", updateItemError)
      console.log(updateItemError.errors[0].message) // errors from Appsync take this format, there may be other formats depending on where the operation fails
    }
    else if (updateItemResponse) {
      console.log(updateItemResponse)
      console.log('Item updated: ', updateItemResponse.data.updateAuctionItem.ItemId)
      queryClient.invalidateQueries(['adminAuctionDetail'])
      setShowModal(false)
      setSelectedItems([])
      setSelectedFixedItems([])
      setSelectedDonation(false)
    }
  }, [updateItemResponse, updateItemError, isPending, queryClient])

    //for update fixed item
  useEffect(() => {
    showLoading(isFixedItemPending)

    console.log('updateFixedItemResponse', updateFixedItemResponse)
    console.log('updateFixedItemError', updateFixedItemError)

    if (updateFixedItemError) {
      console.log("Item not updated: ", updateFixedItemError)
      console.log(updateFixedItemError.errors[0].message) // errors from Appsync take this format, there may be other formats depending on where the operation fails
    }
    else if (updateFixedItemResponse) {
      console.log(updateFixedItemResponse)
      console.log('Item updated: ', updateFixedItemResponse.data.updateFixedItem.FixedItemId)
      queryClient.invalidateQueries(['adminAuctionDetail'])
      setShowModal(false)
      setSelectedItems([])
      setSelectedFixedItems([])
      setSelectedDonation(false)
    }
  }, [updateFixedItemResponse, updateFixedItemError, isFixedItemPending, queryClient])



  //for make donation
  useEffect(() => {
    showLoading(isDonationPending)

    console.log('updateDonationResponse', updateDonationResponse)
    console.log('updateDonationError', updateDonationError)

    if (updateDonationError) {
      console.log("Registration not updated: ", updateDonationError)
      console.log(updateDonationError.errors[0].message) // errors from Appsync take this format, there may be other formats depending on where the operation fails
    }
    else if (updateDonationResponse) {
      console.log(updateDonationResponse)
      console.log('Registration updated: ', updateDonationResponse.data.adminUpdateRegistration.RegistrationId)
      queryClient.invalidateQueries(['adminAuctionDetail'])
      setShowModal(false)
      setSelectedItems([])
      setSelectedFixedItems([])
      setSelectedDonation(false)
    }
  }, [updateDonationResponse, updateDonationError, isDonationPending, queryClient])



  //for make admin payment
  useEffect(() => {
    showLoading(adminMakePaymentPending)

    if (adminMakePaymentError) {
      console.log("Item not updated: ", adminMakePaymentError)
      console.log(adminMakePaymentError.errors[0].message) // errors from Appsync take this format, there may be other formats depending on where the operation fails
    }
    else if (adminMakePaymentResponse) {
      console.log(adminMakePaymentResponse)
      console.log('Item updated: ', adminMakePaymentResponse.data)
      queryClient.invalidateQueries(['adminAuctionDetail'])
      setShowModal(false)
      setSelectedItems([])
      setSelectedFixedItems([])
      setSelectedDonation(false)
    }

  }, [adminMakePaymentResponse, adminMakePaymentError, adminMakePaymentPending, queryClient])


  //for loading - check all pending and loadings inside the component to override
  useEffect(() => {

    if (adminMakePaymentPending || isLoading || isPending || isFixedItemPending || isDonationPending) showLoading(true)
    else showLoading(false)

  }, [adminMakePaymentPending, isLoading, isPending, isFixedItemPending, isDonationPending])

  useEffect(() => {
    if (!auctionDetail) return

    const adminItems = auctionDetail?.Items || []
    const adminFixedItems = auctionDetail?.FixedItems || []
    const registrations = auctionDetail?.Registrations || []

    const userItems = adminItems.filter((item) => item.CurrentHighBidder === userId)
    const userFixedItems = adminFixedItems.map((item) => buildManageCheckoutFixedItemRow(item, userId)).filter(Boolean)
    const userRegistration = registrations.find((registration) => registration?.User?.UserId === userId)
    const userDonation = buildManageCheckoutDonationRow(userRegistration)

    const displayRows = [
      ...userItems.map((item) => buildManageCheckoutBidItemRow(item)),
      ...userFixedItems,
    ]

    if (userDonation) displayRows.push(userDonation)

    const paidCount = displayRows.reduce((sum, row) => sum + (row.Paid ? 1 : 0), 0)

    setItemsPaidCount(paidCount)
    setItemsUnpaidCount(displayRows.length - paidCount)
    setAttendeeItems(userItems)
    setAttendeeFixedItems(userFixedItems)
    setAttendeeDonation(userDonation)
    setAttendeeCheckoutRows(displayRows)
    setSelectedItems([])
    setSelectedFixedItems([])
    setSelectedDonation(false)

  }, [auctionDetail, userId])

  useEffect(() => {
    const itemTotal = attendeeItems.reduce((sum, item) => {
      if (selectedItems.includes(item.ItemId)) sum += item.CurrentBid
      return sum
    }, 0)

    const fixedItemTotal = attendeeFixedItems.reduce((sum, item) => {
      if (selectedFixedItems.includes(item.FixedItemId)) sum += item.AmountDue
      return sum
    }, 0)

    const donationTotal = selectedDonation ? (attendeeDonation?.AmountDue || 0) : 0

    setChargeTotal(itemTotal + fixedItemTotal + donationTotal)

  }, [selectedItems, selectedFixedItems, selectedDonation, attendeeItems, attendeeFixedItems, attendeeDonation])

  const toggleCheckbox = (row) => {
    if (row.Paid) return

    if (row.rowType === 'item') {
      const itemIndex = selectedItems.indexOf(row.ItemId)
      const nextItems = [...selectedItems]

      if (itemIndex > -1) nextItems.splice(itemIndex, 1)
      else nextItems.push(row.ItemId)

      setSelectedItems(nextItems)
      return
    }

    if (row.rowType === 'fixedItem') {
      const fixedItemIndex = selectedFixedItems.indexOf(row.FixedItemId)
      const nextFixedItems = [...selectedFixedItems]

      if (fixedItemIndex > -1) nextFixedItems.splice(fixedItemIndex, 1)
      else nextFixedItems.push(row.FixedItemId)

      setSelectedFixedItems(nextFixedItems)
      return
    }

    if (row.rowType === 'donation') {
      setSelectedDonation(!selectedDonation)
    }
  }

  const selectAllItems = (checked) => {
    if (!checked) {
      setSelectedItems([])
      setSelectedFixedItems([])
      setSelectedDonation(false)
      return
    }

    setSelectedItems(attendeeItems.filter((item) => !item.Paid).map((item) => item.ItemId))
    setSelectedFixedItems(attendeeFixedItems.filter((item) => !item.Paid).map((item) => item.FixedItemId))
    setSelectedDonation(!!(attendeeDonation && !attendeeDonation.Paid))
  }

  const countItems = () => {
    return selectedItems.length + selectedFixedItems.length + (selectedDonation ? 1 : 0)
  }

  const isRowSelected = (row) => {
    if (row.rowType === 'item') return selectedItems.indexOf(row.ItemId) > -1
    if (row.rowType === 'fixedItem') return selectedFixedItems.indexOf(row.FixedItemId) > -1
    if (row.rowType === 'donation') return selectedDonation
    return false
  }

  if (!auctionDetail || !attendeeCheckoutRows) return (<></>)

  const sortInput = sortOptions.key === '' ? { key: 'Title', direction: 'asc' } : sortOptions
  const sortedItems = performSort(attendeeCheckoutRows, sortInput)
  const selectableRowsCount = attendeeCheckoutRows.filter((row) => !row.Paid).length
  const allSelectableRowsChecked = selectableRowsCount > 0 && countItems() === selectableRowsCount
  const attendeeName = managedAttendee?.FullName || `${managedAttendee?.User?.FirstName || ''} ${managedAttendee?.User?.LastName || ''}`.trim()
  const disableMarkAsPaid = ( selectedItems.length + selectedFixedItems.length + (selectedDonation ? 1 : 0) ) !== 1
  const showFairMarketValueNotice = attendeeCheckoutRows.some((row) => {
    if (row.rowType === 'item') return row.CurrentBid > row.Value
    if (row.rowType === 'fixedItem') return row.TotalAmount > ((row.Value || 0) * (row.Quantity || 0))
    return false
  })

  return (
    <div>

      {(showModal && modalUse === 'confirmCharges') && <ConfirmMakePayment setShowConfirmModal={setShowModal} data={modalData} adminCharge={adminCharge} /> }
      {(showModal && modalUse === 'markItemAsPaid') && <ConfirmMarkAsPaid setShowConfirmModal={setShowModal} data={modalData} updateItem={updateItem} /> }
      {(showModal && modalUse === 'markFixedItemAsPaid') && <ConfirmMarkFixedItemAsPaid setShowConfirmModal={setShowModal} data={modalData} updateFixedItem={updateFixedItem} /> }
      {(showModal && modalUse === 'markDonationAsPaid') && <ConfirmMarkDonationAsPaid setShowConfirmModal={setShowModal} data={modalData} updateDonation={updateDonation} /> }

      <div className={css['closed-auction-attendee-heading']}>
        <div className={`${css['back-to-attendee-link']}`} onClick={() => toAllAttendeePage()}>
          ⬅ Back to All Attendees
        </div>
        <p>Viewing attendee:</p>
        <h2>{attendeeName}</h2>
      </div>

      <div className={css['closed-auction-summary']}>
        <div>
          <p>{itemsUnpaidCount} ITEM(S) WITH BALANCE DUE</p>

        </div>
        <div>
          <p>{itemsPaidCount} ITEM(S) WITH FULLY PAID BALANCE</p>
        </div>
      </div>

      <div className={css['closed-auction-charge-selected']}>
        <p>
          {countItems()} ITEM(S) SELECTED
        </p>
        <button disabled={countItems() < 1}
          onClick={() => chargeAmountDueAction()}
          className={`${themeCss['button']} ${themeCss['FilledBlue']}`}>
          Charge Amount Due
        </button>

        <button disabled={disableMarkAsPaid}
          onClick={() => markAsPaid()}
          className={`${themeCss['button']} ${themeCss['FilledWhite']}`}>
          Mark As Paid
        </button>
        <p className={css['closed-auction-charge-note']}>
          'Mark as Paid' supports one line item at a time. 
        </p>
        {attendeeFixedItems?.length > 0 && <p className={css['closed-auction-charge-note']}>
          Marking a fixed price item as paid applies to all quantities.
        </p>}
      </div>

      {showFairMarketValueNotice && <p className={themeCss['boldGreen']}>Items in bold green are above Fair Market Value:</p>}

      <table className={themeCss['data-table']}>
        <thead>
          <tr>
            <th>
              <input type='checkbox' id='adminManageCheckoutSelectAll' onChange={(e) => selectAllItems(e.target.checked)}
                checked={allSelectableRowsChecked}
              />
            </th>
            {tableFormatter.map((ele) => {
            return (
              <th onClick={() => handleSort(ele[0], sortOptions, setSortOptions)} key={ele[1]}>
                {ele[1]} {sortSpan(ele[0], sortOptions)}</th>
              )})}
          </tr>
        </thead>
        <tbody>
          {sortedItems && sortedItems.map((item) => {
          return <ClosedManageCheckoutRow key={item.rowId}
                  isSelected={isRowSelected(item)} toggleCheckbox={toggleCheckbox}
                  item={item} />
          })}
        </tbody>
      </table>

    </div>
  )
}
